/**
 * The domain vocabulary the console renders, and the talent and organization lists.
 *
 * The shapes are the API's schemas (`./schemas`). The constants exist so a status pill can
 * be typed rather than stringly-compared; each is checked against the API's enum, so a
 * value it does not send is a type error rather than a pill nobody will ever see.
 *
 * What used to be in this file and is not: credential kinds, organization kinds, plan
 * codes, feature flags, and the BullMQ queue and job names. Every one of them had zero
 * readers here — they are what the API and the worker do with a row, not what the console
 * shows about it.
 */
import type { Covers, Schemas } from './schemas';

/**
 * Where a talent or an organization is in the manual verification loop (C5), as the
 * console's pills name it. Console vocabulary, not a wire enum: the API sends a talent's
 * `verified` flag and an organization's `verificationStatus`, and a screen maps either
 * onto one of these.
 */
export const VERIFICATION_STATUS = {
  UNVERIFIED: 'unverified',
  PENDING: 'pending',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
} as const;
export type VerificationStatus = (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS];

/** The document pipeline in ADR-009, in order. */
export type DocumentStatus = Schemas['DocumentStatus'];
export const DOCUMENT_STATUS = {
  PENDING: 'pending',
  SCANNING: 'scanning',
  AWAITING_AUDIT: 'awaiting_audit',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  INFECTED: 'infected',
} as const satisfies Record<string, DocumentStatus>;
const _documentStatusCovered: Covers<
  DocumentStatus,
  (typeof DOCUMENT_STATUS)[keyof typeof DOCUMENT_STATUS]
> = true;

export type SubscriptionStatus = Schemas['SubscriptionStatus'];
export const SUBSCRIPTION_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  GRACE: 'grace',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const satisfies Record<string, SubscriptionStatus>;
const _subscriptionStatusCovered: Covers<
  SubscriptionStatus,
  (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS]
> = true;

/**
 * A row of the ministry specialty catalogue (ADR-004). Reference data, and the same list
 * the mobile app and the public profile read. It changes with a deploy, never with a click.
 */
export type Specialty = Schemas['SpecialtyDto'];

/**
 * One row of the admin talent search. The schema is shared with the club search, whose
 * `contact` the admin-scoped route never fills: an admin searches to moderate, and
 * moderation needs a name and a status, not a phone number.
 */
export type TalentSearchHit = Schemas['TalentSearchResultDto'];

/** The longest reason `DELETE /admin/talents/:id/photo` accepts — the form stops there too. */
export const TALENT_PHOTO_REASON_MAX_LENGTH = 300;

/**
 * The API's cursor-page envelope, shared by every keyset-paginated list (the talent
 * search, the support inbox). `nextCursor` is present only when `hasMore`; the next
 * page is asked for with `?cursor=<nextCursor>` and the same filters as the first.
 */
export type CursorPageMeta = Schemas['CursorPageMetaDto'];

export type TalentSearchPage = Schemas['TalentSearchResponseDto'];

/** The API's offset-page envelope (`?page=&perPage=`). */
export type PageMeta = Schemas['PageMetaDto'];

/**
 * The account kinds an organization registers as — the `?kind=` filter on
 * `GET /admin/organizations` and the enum the row's `kind` is one of.
 */
export type OrganizationKind = Schemas['OrganizationKind'];
export const ORGANIZATION_KIND = {
  CLUB: 'club',
  FEDERATION: 'federation',
  ACADEMY: 'academy',
  AGENT: 'agent',
  SPORTS_INSTITUTION: 'sports_institution',
  OTHER: 'other',
} as const satisfies Record<string, OrganizationKind>;
const _organizationKindCovered: Covers<
  OrganizationKind,
  (typeof ORGANIZATION_KIND)[keyof typeof ORGANIZATION_KIND]
> = true;

/** One organization as the console's clubs grid renders it (C5). */
export type AdminOrganization = Schemas['AdminOrganizationDto'];

export type AdminOrganizationPage = Schemas['AdminOrganizationListDto'];
